import { RectangleShape, AbstractCanvas2D } from '@maxgraph/core';

/**
 * Compact rounded-rectangle pill for operation nodes.
 *
 * Recommended dimensions (set by caller via cell style): 100 × 28 px.
 *
 * Renders a rounded pill, then paints a 3 px wide colored stripe on the
 * left edge whose color is determined by the cell's `archetype` style
 * property. Unknown archetypes render a neutral gray stripe.
 */
export class OperationPillShape extends RectangleShape {
  paintBackground(
    c: AbstractCanvas2D,
    x: number,
    y: number,
    w: number,
    h: number
  ): void {
    const r = Math.min(h / 2, 6);

    // ── Rounded pill outline ────────────────────────────────────────────
    c.begin();
    c.moveTo(x + r, y);
    c.lineTo(x + w - r, y);
    c.quadTo(x + w, y, x + w, y + r);
    c.lineTo(x + w, y + h - r);
    c.quadTo(x + w, y + h, x + w - r, y + h);
    c.lineTo(x + r, y + h);
    c.quadTo(x, y + h, x, y + h - r);
    c.lineTo(x, y + r);
    c.quadTo(x, y, x + r, y);
    c.close();
    c.fillAndStroke();

    // ── Archetype accent stripe (left edge) ─────────────────────────────
    const archetype = (this.style as Record<string, unknown>)
      ?.archetype as string | undefined;
    if (archetype) {
      c.setFillColor(getArchetypeColor(archetype));
      c.begin();
      c.moveTo(x, y + r);
      c.lineTo(x + 3, y + r);
      c.lineTo(x + 3, y + h - r);
      c.lineTo(x, y + h - r);
      c.close();
      c.fill();
    }
  }
}

function getArchetypeColor(archetype: string): string {
  switch (archetype) {
    case 'TRANSFORM':
      return '#3B82F6';
    case 'TRANSFER':
      return '#10B981';
    case 'MEASURE':
      return '#F59E0B';
    case 'COMPLEX':
      return '#8B5CF6';
    default:
      return '#6B7280';
  }
}
